import React, { useEffect, useMemo, useRef } from "react";
import type { UniversalMediaPlayer } from "universal-media-player";
import { setTranslationLoader } from "universal-media-player";
import useTelemetry from "../../../utils/Telemetry/useTelemetry";
import { VideoEventNameToBehavior } from "./VideoPlayer.constants";
import type {
  CaptionType,
  DownloadableType,
  JsonCaption,
  UMPPlayerState,
  VideoCustomEventType,
} from "./VideoPlayer.types";
import { createPlayerOptions, onVideoEvent } from "./VideoPlayer.utils";

setTranslationLoader(
  (locale) => import(`universal-media-player/locales/${locale}.mjs`)
);

export const convertJsonToCaptionArray = (
  jsonString: string
): CaptionType[] => {
  if (!jsonString || jsonString === "") {
    return [];
  }

  try {
    const parsedArray: JsonCaption[] = JSON.parse(jsonString);

    const captionArray: CaptionType[] = parsedArray.map(
      (item: JsonCaption) => ({
        url: item.Url,
        locale: item.language,
      })
    );

    captionArray.forEach((caption) => {
      const ccFileExt = caption?.url?.split(".").pop();
      if (
        ccFileExt?.toLowerCase() === "vtt" ||
        ccFileExt?.toLocaleLowerCase() === "ttml"
      ) {
        caption.ccType = ccFileExt.toUpperCase();
      }
    });

    return captionArray;
  } catch (error) {
    return [];
  }
};

export const createDownloadableFiles = (
  captionArray: CaptionType[],
  url: string,
  locale: string
): DownloadableType[] => {
  // create return array of downloadable files
  const downloadableFiles: DownloadableType[] = captionArray.map((caption) => ({
    url: caption.url,
    locale: caption.locale,
    mediaType: "transcript",
  }));

  // add video file to downloadable files
  downloadableFiles.push({
    url,
    locale,
    mediaType: "video",
  });

  return downloadableFiles;
};

const VideoPlayer: React.FC<VideoProps> = (props: VideoProps) => {
  const {
    poster,
    videoId,
    videoTitle,
    autoplay = false,
    locale,
    highQualityVideoUrl,
    mediumQualityVideoUrl,
    lowQualityVideoUrl,
    adaptiveVideoDashUrl,
    adaptiveVideoHLSUrl,
    captions,
  } = props;
  const ocpVideoClass = "ocpVideo";
  const id = `${ocpVideoClass}`;
  const isMute = useRef(false);
  const playerRef = React.createRef<UniversalMediaPlayer>();
  const telemetry = useTelemetry();

  useEffect(() => {
    // bind all video events
    for (const [eventName] of VideoEventNameToBehavior) {
      playerRef.current?.addEventListener(eventName, () => {
        if (!playerRef.current) return;

        const {
          currentTime,
          duration: totalDuration,
          muted,
        } = (playerRef.current as UniversalMediaPlayer)
          .currentState as UMPPlayerState;

        const updateMutedState = (muted: boolean) => {
          isMute.current = muted;
        };

        onVideoEvent({
          event: eventName,
          id,
          videoId: videoId as string,
          videoTitle: videoTitle as string,
          duration: totalDuration,
          currentTime,
          currMuteState: isMute.current,
          muted,
          updateMutedState,
          telemetry,
        } as VideoCustomEventType);
      });
    }
  });
  const captionArray = convertJsonToCaptionArray(captions);
  const downloadableFiles = createDownloadableFiles(
    captionArray,
    highQualityVideoUrl,
    locale
  );
  const options = useMemo(
    () =>
      JSON.stringify({
        ...createPlayerOptions(
          videoId as string,
          videoTitle as string,
          locale,
          autoplay,
          highQualityVideoUrl as string,
          mediumQualityVideoUrl as string,
          lowQualityVideoUrl as string,
          adaptiveVideoDashUrl as string,
          adaptiveVideoHLSUrl as string,
          poster as string,
          captionArray as Array<CaptionType>,
          downloadableFiles as Array<DownloadableType>
        ),
      }),
    []
  );

  return (
    <universal-media-player
      id={id}
      ref={playerRef}
      options={options}
      data-testid={id}
    />
  );
};

export type VideoProps = {
  position?: number;
  videoId?: string;
  videoTitle?: string;
  videoType?: string;
  autoplay?: boolean;
  locale: string;
  isLearnVideo: boolean;
  highQualityVideoUrl: string;
  mediumQualityVideoUrl: string;
  lowQualityVideoUrl: string;
  adaptiveVideoDashUrl: string;
  adaptiveVideoHLSUrl: string;
  poster?: string;
  captions: string;
};

export default VideoPlayer;
